import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import CryptoJS from 'crypto-js'; // Imported for secure Cloudinary signature parameters

function RoomEditModal({ room, onClose, onSaveSuccess }) {
  const roomTypeOptions = ['Single Room', '1 BHK', '2BHK', 'Shared Rooms', 'Flat', 'Others'];
  const furnishingOptions = ['Furnished', 'Semi Furnished', 'Unfurnished'];
  const statusOptions = ['active', 'rented', 'inactive'];

  const [formData, setFormData] = useState({
    title: room.title || '',
    description: room.description || '',
    price: room.price || 0,
    place: room.place || '',
    roomType: room.roomType || roomTypeOptions[0],
    furnishingStatus: room.furnishingStatus || furnishingOptions[0],
    status: room.status || 'active',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Cloudinary credentials configuration matching your project specs
  const CLOUD_NAME = 'dnlaudxq2';
  const API_KEY = '396776948922162';
  const API_SECRET = 'rxKOlhA8rOtUKDOZPfFGEQju29A';

  useEffect(() => {
    setFormData({
      title: room.title || '',
      description: room.description || '',
      price: room.price || 0,
      place: room.place || '',
      roomType: room.roomType || roomTypeOptions[0],
      furnishingStatus: room.furnishingStatus || furnishingOptions[0],
      status: room.status || 'active',
    });
  }, [room]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'price' ? parseFloat(value) || 0 : value
    }));
  };

  // Helper utility to safely execute image deletion requests
  const deleteFromCloudinary = async (publicId) => {
    const timestamp = Math.round(new Date().getTime() / 1000);
    const stringToSign = `public_id=${publicId}&timestamp=${timestamp}${API_SECRET}`;
    const signature = CryptoJS.SHA1(stringToSign).toString();

    const formData = new FormData();
    formData.append('public_id', publicId);
    formData.append('signature', signature);
    formData.append('api_key', API_KEY);
    formData.append('timestamp', timestamp);

    try {
      await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/destroy`, {
        method: 'POST',
        body: formData,
      });
      console.log(`Cloudinary image asset wiped: ${publicId}`);
    } catch (err) {
      console.error("Cloudinary server API handshake fault:", err);
    }
  };

  // NEW FUNCTION: Completely clear room images and document files
  const handleDeleteRoom = async () => {
    if (!window.confirm(`⚠️ PERMANENT DELETION WARNING:\n\nAre you sure you want to completely destroy this room listing?\n\nThis will permanently delete all metadata from Firestore and wipe all listed photos from Cloudinary. This cannot be undone.`)) return;

    setLoading(true);
    setError('');
    try {
      // 1. Loop check through array blocks and execute signed asset deletion calls
      if (room.imageUrls && room.imageUrls.length > 0) {
        console.log(`Found ${room.imageUrls.length} images. Clearing cloud storage assets...`);
        for (const image of room.imageUrls) {
          const publicId = typeof image === 'object' ? image.public_id : null;
          if (publicId) {
            await deleteFromCloudinary(publicId);
          }
        }
      }

      // 2. Clear listing node document file from collection directory
      const roomDocRef = doc(db, 'roomsData', room.ownerId, 'rooms', room.id);
      await deleteDoc(roomDocRef);

      alert('Room listing and images removed successfully!');
      onSaveSuccess(); // Refresh master datasets
      onClose(); // Close out view
    } catch (err) {
      setError(`Critical deletion error occurred: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const roomDocRef = doc(db, 'roomsData', room.ownerId, 'rooms', room.id);
      await updateDoc(roomDocRef, formData);
      alert('Room updated successfully!');
      onSaveSuccess();
      onClose();
    } catch (err) {
      setError(`Failed to update: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.modalOverlay}>
      <style>{`
        @media (max-width: 500px) {
          .modal-content { width: 95% !important; padding: 20px !important; }
          .btn-row-layout { flex-direction: column !important; gap: 8px !important; }
        }
      `}</style>

      <div style={styles.modalContent} className="modal-content">
        <button onClick={onClose} style={styles.closeBtn} disabled={loading}>×</button>
        <h2 style={styles.title}>Manage: {room.title}</h2>
        <div style={{fontSize: '11px', color: '#64748b', textAlign: 'center', marginBottom: '15px', marginTop: '-15px'}}>Owner ID: {room.ownerId}</div>
        
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGrid}>
            <input name="title" value={formData.title} onChange={handleChange} placeholder="Title" style={styles.input} required disabled={loading} />
            <input name="place" value={formData.place} onChange={handleChange} placeholder="Location" style={styles.input} required disabled={loading} />
            <input type="number" name="price" value={formData.price} onChange={handleChange} placeholder="Price (Nrs)" style={styles.input} required disabled={loading} />
            
            <select name="roomType" value={formData.roomType} onChange={handleChange} style={styles.input} disabled={loading}>
              {roomTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>

            <select name="furnishingStatus" value={formData.furnishingStatus} onChange={handleChange} style={styles.input} disabled={loading}>
              {furnishingOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>

            <select name="status" value={formData.status} onChange={handleChange} style={{...styles.input, borderColor: '#3b82f6'}} disabled={loading}>
              {statusOptions.map(opt => <option key={opt} value={opt}>{opt.toUpperCase()}</option>)}
            </select>
          </div>
          
          <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Description" style={styles.textarea} required disabled={loading} />

          {error && <p style={styles.error}>{error}</p>}
          
          {/* Dynamic multi-action toolbar button controller box */}
          <div style={styles.btnRow} className="btn-row-layout">
            <button type="button" onClick={handleDeleteRoom} disabled={loading} style={styles.deleteBtn}>
              {loading ? 'Processing...' : 'Delete Room Listing'}
            </button>
            <div style={{display: 'flex', gap: '10px', flex: 1.5}}>
              <button type="button" onClick={onClose} style={styles.cancelBtn} disabled={loading}>Cancel</button>
              <button type="submit" disabled={loading} style={styles.saveBtn}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  /* Overlay & Layout Containers */
  modalOverlay: { 
    position: 'fixed', 
    top: 0, 
    left: 0, 
    right: 0, 
    bottom: 0, 
    backgroundColor: 'rgba(0,0,0,0.6)', 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    zIndex: 1000, 
    padding: '10px' 
  },

  modalContent: { 
    backgroundColor: '#fff', 
    padding: '30px', 
    borderRadius: '16px', 
    width: '520px', 
    position: 'relative', 
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' 
  },

  /* Typography & Core Elements */
  closeBtn: { 
    position: 'absolute', 
    top: '15px', 
    right: '15px', 
    background: 'none', 
    border: 'none', 
    fontSize: '24px', 
    cursor: 'pointer', 
    color: '#64748b' 
  },

  title: { 
    margin: '0 0 20px 0', 
    fontSize: '20px', 
    color: '#1e293b', 
    textAlign: 'center', 
    fontWeight: '600' 
  },

  /* Form & Inputs */
  form: { 
    display: 'flex', 
    flexDirection: 'column', 
    gap: '15px' 
  },

  formGrid: { 
    display: 'grid', 
    gridTemplateColumns: '1fr 1fr', 
    gap: '10px' 
  },

  input: { 
    padding: '12px', 
    border: '1px solid #e2e8f0', 
    borderRadius: '8px', 
    fontSize: '14px', 
    outline: 'none', 
    backgroundColor: '#fff', 
    color: '#334155' 
  },

  textarea: { 
    padding: '12px', 
    border: '1px solid #e2e8f0', 
    borderRadius: '8px', 
    fontSize: '14px', 
    minHeight: '80px', 
    resize: 'vertical', 
    color: '#334155', 
    outline: 'none' 
  },

  /* Buttons & Message States */
  btnRow: { 
    display: 'flex', 
    gap: '10px', 
    marginTop: '15px', 
    width: '100%' 
  },
  
  saveBtn: { 
    flex: 1, 
    padding: '12px', 
    backgroundColor: '#3b82f6', 
    color: 'white', 
    border: 'none', 
    borderRadius: '8px', 
    cursor: 'pointer', 
    fontWeight: '600', 
    fontSize: '13px' 
  },

  cancelBtn: { 
    flex: 1, 
    padding: '12px', 
    backgroundColor: '#f1f5f9', 
    color: '#475569', 
    border: 'none', 
    borderRadius: '8px', 
    cursor: 'pointer', 
    fontWeight: '600', 
    fontSize: '13px' 
  },

  deleteBtn: { 
    flex: 1, 
    padding: '12px', 
    backgroundColor: '#fee2e2', 
    color: '#991b1b', 
    border: '1px solid #fca5a5', 
    borderRadius: '8px', 
    cursor: 'pointer', 
    fontWeight: '600', 
    fontSize: '13px', 
    transition: '0.2s' 
  },

  error: { 
    color: '#ef4444', 
    fontSize: '12px', 
    textAlign: 'center', 
    fontWeight: '500' 
  }
};

export default RoomEditModal;